/**
 * RichTextEditor — Shared TipTap rich text editor
 * Configurable toolbar features for different contexts (email, tickets, articles)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Placeholder from '@tiptap/extension-placeholder';
import type { EditorView } from '@tiptap/pm/view';
import { Icon } from '@/shared/icons';
import { Tooltip } from './Tooltip';
import { ResizableImage, insertResizableImageAt } from './richtext/ResizableImage';

export interface RichTextEditorFeatures {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  textAlign?: boolean;
  bulletList?: boolean;
  orderedList?: boolean;
  heading?: boolean;
  link?: boolean;
  image?: boolean;
  attachment?: boolean;
  blockquote?: boolean;
  code?: boolean;
  codeBlock?: boolean;
}

const defaultFeatures: Required<RichTextEditorFeatures> = {
  bold: true,
  italic: true,
  underline: true,
  textAlign: true,
  bulletList: true,
  orderedList: true,
  heading: false,
  link: true,
  image: true,
  attachment: false,
  blockquote: false,
  code: false,
  codeBlock: false,
};

export interface RichTextEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  showToolbar?: boolean;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  features?: RichTextEditorFeatures;
  /** Uploads a single image file and resolves with the URL to embed inline (`setImage`). */
  onImageUpload?: (file: File) => Promise<string>;
  onFileAttach?: (files: FileList) => void;
  /** Called on Ctrl+Enter / Cmd+Enter. When absent, the shortcut falls back to default editor behavior. */
  onSubmitShortcut?: () => void;
}

// Minimal styling for elements the Tailwind `prose` class (unused — no
// @tailwindcss/typography plugin registered in this project) would normally
// cover: inline code, code blocks and blockquotes. Scoped to this component's
// ProseMirror content so it doesn't leak to other `.ProseMirror` instances.
const editorStyles = `
  .richtext-editor-content .ProseMirror pre {
    background-color: var(--theme-input-background, #f3f4f6);
    color: var(--theme-card-text, #1f2937);
    padding: 0.75rem;
    border-radius: 0.375rem;
    overflow-x: auto;
  }
  .richtext-editor-content .ProseMirror pre,
  .richtext-editor-content .ProseMirror code {
    font-family: var(--theme-typography-font-family-mono, monospace);
  }
  .richtext-editor-content .ProseMirror pre code {
    background: none;
    padding: 0;
    font-size: 0.875em;
  }
  .richtext-editor-content .ProseMirror code {
    background-color: var(--theme-input-background, #f3f4f6);
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    font-size: 0.875em;
  }
  .richtext-editor-content .ProseMirror blockquote {
    border-left: 3px solid var(--theme-card-border, #e5e7eb);
    padding-left: 1rem;
    margin-left: 0;
    color: var(--theme-muted-text, #6b7280);
  }
`;

const BlockquoteIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 7h4v4a4 4 0 0 1-4 4" />
    <path d="M15 7h4v4a4 4 0 0 1-4 4" />
  </svg>
);

const CodeBlockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M9 9l-2 3 2 3" />
    <path d="M15 9l2 3-2 3" />
  </svg>
);

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Escreva aqui...',
  minHeight = 200,
  showToolbar = true,
  disabled = false,
  className,
  style,
  features: featuresProp,
  onImageUpload,
  onFileAttach,
  onSubmitShortcut,
}) => {
  const features = { ...defaultFeatures, ...featuresProp };
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const imageInputRef = React.useRef<HTMLInputElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // The `editorProps` handlers below (handleKeyDown/handlePaste/handleDrop)
  // are captured by TipTap when the editor instance is created and are NOT
  // re-created on re-render — reading props directly inside them would freeze
  // the values from the first render (stale closure). Keep the latest
  // callbacks in refs (updated every render) and read `ref.current` inside
  // the handlers.
  const onSubmitShortcutRef = React.useRef(onSubmitShortcut);
  onSubmitShortcutRef.current = onSubmitShortcut;
  const onImageUploadRef = React.useRef(onImageUpload);
  onImageUploadRef.current = onImageUpload;

  // Shared upload+insert flow for paste/drop, where only the ProseMirror
  // `view` (not the higher-level `editor` chain API) is safely available —
  // these handlers are defined inline inside the same `useEditor()` call that
  // produces `editor`, so referencing `editor` there would run into a
  // temporal-dead-zone / use-before-define hazard. Reads `onImageUploadRef`
  // (not the prop) so it stays stable and never goes stale inside editorProps.
  const uploadAndInsertImage = useCallback((view: EditorView, file: File, pos?: number) => {
    const upload = onImageUploadRef.current;
    if (!upload) return;
    setIsUploadingImage(true);
    upload(file)
      .then((url) => {
        insertResizableImageAt(view, url, pos);
      })
      .catch(() => {
        // Upload failure surfacing (toast/etc.) is the consumer's responsibility.
      })
      .finally(() => {
        setIsUploadingImage(false);
      });
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: features.heading ? { levels: [1, 2, 3] } : false,
        bulletList: features.bulletList ? {} : false,
        orderedList: features.orderedList ? {} : false,
        blockquote: features.blockquote ? {} : false,
      }),
      ...(features.underline ? [Underline] : []),
      TextStyle,
      Color,
      ...(features.link ? [Link.configure({ openOnClick: false })] : []),
      ...(features.image ? [ResizableImage] : []),
      ...(features.textAlign ? [TextAlign.configure({ types: ['heading', 'paragraph'] })] : []),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
    editorProps: {
      handleKeyDown(_view, event) {
        const submit = onSubmitShortcutRef.current;
        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && submit) {
          event.preventDefault();
          submit();
          return true;
        }
        return false;
      },
      handlePaste(view, event) {
        if (!onImageUploadRef.current) return false;
        const items = event.clipboardData?.items;
        if (!items) return false;
        const imageItem = Array.from(items).find(
          (item) => item.kind === 'file' && item.type.startsWith('image/')
        );
        const file = imageItem?.getAsFile();
        if (!file) return false;
        event.preventDefault();
        uploadAndInsertImage(view, file);
        return true;
      },
      handleDrop(view, event) {
        if (!onImageUploadRef.current) return false;
        const file = Array.from(event.dataTransfer?.files ?? []).find((candidate) =>
          candidate.type.startsWith('image/')
        );
        if (!file) return false;
        event.preventDefault();
        const dropPos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
        uploadAndInsertImage(view, file, dropPos);
        return true;
      },
    },
  });

  // Sync external value changes into the editor (e.g. when editing loads data)
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  const addLink = useCallback(() => {
    if (!editor || !linkUrl) return;
    editor.chain().focus().setLink({ href: linkUrl }).run();
    setLinkUrl('');
    setShowLinkInput(false);
  }, [editor, linkUrl]);

  const addImage = useCallback(() => {
    if (onImageUpload) {
      imageInputRef.current?.click();
    } else if (editor) {
      const url = prompt('URL da imagem:');
      if (url) {
        editor.chain().focus().setImage({ src: url }).run();
      }
    }
  }, [editor, onImageUpload]);

  const handleAttach = useCallback(() => {
    if (onFileAttach) {
      fileInputRef.current?.click();
    }
  }, [onFileAttach]);

  if (!editor) return null;

  // F80: rótulo do botão só-ícone via Tooltip shared (Radix) — nunca `title=` nativo.
  const ToolbarButton: React.FC<{
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    title?: string;
    testId?: string;
  }> = ({ onClick, isActive, disabled: buttonDisabled, children, title, testId }) => {
    const button = (
      <button
        type="button"
        onClick={onClick}
        disabled={buttonDisabled}
        data-testid={testId}
        className="p-1.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          color: isActive ? 'var(--theme-primary)' : 'var(--theme-muted-text)',
          backgroundColor: isActive ? 'var(--theme-button-ghost-hover)' : 'transparent',
        }}
        onMouseEnter={(e) => {
          if (!isActive) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
        }}
        onMouseLeave={(e) => {
          if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {children}
      </button>
    );

    return title ? <Tooltip content={title}>{button}</Tooltip> : button;
  };

  const Separator = () => (
    <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--theme-card-border)' }} />
  );

  return (
    <div className={`flex flex-col ${className || ''}`} style={{ minHeight, ...style }}>
      {showToolbar && (
        <div
          className="flex items-center gap-0.5 px-2 py-1.5 border-b flex-wrap"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            borderColor: 'var(--theme-card-border)',
          }}
        >
          {features.heading && (
            <>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                isActive={editor.isActive('heading', { level: 2 })}
                title="Título"
                testId="richtext-heading-button"
              >
                <Icon name="type" className="h-4 w-4" />
              </ToolbarButton>
              <Separator />
            </>
          )}

          {features.bold && (
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={editor.isActive('bold')}
              title="Negrito"
              testId="richtext-bold-button"
            >
              <Icon name="bold" className="h-4 w-4" />
            </ToolbarButton>
          )}
          {features.italic && (
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={editor.isActive('italic')}
              title="Itálico"
              testId="richtext-italic-button"
            >
              <Icon name="italic" className="h-4 w-4" />
            </ToolbarButton>
          )}
          {features.underline && (
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              isActive={editor.isActive('underline')}
              title="Sublinhado"
              testId="richtext-underline-button"
            >
              <Icon name="underline" className="h-4 w-4" />
            </ToolbarButton>
          )}

          {features.textAlign && (
            <>
              <Separator />
              <ToolbarButton
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                isActive={editor.isActive({ textAlign: 'left' })}
                title="Alinhar esquerda"
                testId="richtext-align-left-button"
              >
                <Icon name="align-left" className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                isActive={editor.isActive({ textAlign: 'center' })}
                title="Centralizar"
                testId="richtext-align-center-button"
              >
                <Icon name="align-center" className="h-4 w-4" />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                isActive={editor.isActive({ textAlign: 'right' })}
                title="Alinhar direita"
                testId="richtext-align-right-button"
              >
                <Icon name="align-right" className="h-4 w-4" />
              </ToolbarButton>
            </>
          )}

          {(features.bulletList || features.orderedList) && <Separator />}
          {features.bulletList && (
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              isActive={editor.isActive('bulletList')}
              title="Lista com marcadores"
              testId="richtext-bullet-list-button"
            >
              <Icon name="list" className="h-4 w-4" />
            </ToolbarButton>
          )}
          {features.orderedList && (
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              isActive={editor.isActive('orderedList')}
              title="Lista numerada"
              testId="richtext-ordered-list-button"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="10" y1="6" x2="21" y2="6" />
                <line x1="10" y1="12" x2="21" y2="12" />
                <line x1="10" y1="18" x2="21" y2="18" />
                <text x="4" y="7" fontSize="7" fill="currentColor" stroke="none" textAnchor="middle">1</text>
                <text x="4" y="13" fontSize="7" fill="currentColor" stroke="none" textAnchor="middle">2</text>
                <text x="4" y="19" fontSize="7" fill="currentColor" stroke="none" textAnchor="middle">3</text>
              </svg>
            </ToolbarButton>
          )}

          {(features.blockquote || features.code || features.codeBlock) && <Separator />}
          {features.blockquote && (
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              isActive={editor.isActive('blockquote')}
              title="Citação"
              testId="richtext-blockquote-button"
            >
              <BlockquoteIcon className="h-4 w-4" />
            </ToolbarButton>
          )}
          {features.code && (
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleCode().run()}
              isActive={editor.isActive('code')}
              title="Código inline"
              testId="richtext-code-button"
            >
              <Icon name="code" className="h-4 w-4" />
            </ToolbarButton>
          )}
          {features.codeBlock && (
            <ToolbarButton
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
              isActive={editor.isActive('codeBlock')}
              title="Bloco de código"
              testId="richtext-codeblock-button"
            >
              <CodeBlockIcon className="h-4 w-4" />
            </ToolbarButton>
          )}

          {(features.link || features.image || features.attachment) && <Separator />}
          {features.link && (
            <ToolbarButton
              onClick={() => setShowLinkInput(!showLinkInput)}
              isActive={editor.isActive('link')}
              title="Link"
              testId="richtext-link-button"
            >
              <Icon name="link" className="h-4 w-4" />
            </ToolbarButton>
          )}
          {features.image && (
            <ToolbarButton
              onClick={addImage}
              title="Imagem"
              disabled={isUploadingImage}
              testId="richtext-image-button"
            >
              {isUploadingImage ? (
                <Icon name="loader" className="h-4 w-4 animate-spin" />
              ) : (
                <Icon name="image" className="h-4 w-4" />
              )}
            </ToolbarButton>
          )}
          {features.attachment && (
            <ToolbarButton onClick={handleAttach} title="Anexar arquivo" testId="richtext-attach-button">
              <Icon name="paperclip" className="h-4 w-4" />
            </ToolbarButton>
          )}

          {showLinkInput && (
            <div className="flex items-center gap-1 ml-2">
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://..."
                className="px-2 py-1 text-sm rounded border"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  borderColor: 'var(--theme-input-border)',
                  color: 'var(--theme-input-text)',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addLink();
                  if (e.key === 'Escape') setShowLinkInput(false);
                }}
              />
              <button
                onClick={addLink}
                className="px-2 py-1 text-xs rounded"
                style={{ backgroundColor: 'var(--theme-primary)', color: '#fff' }}
              >
                OK
              </button>
            </div>
          )}
        </div>
      )}

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="richtext-image-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file || !onImageUpload || !editor) return;
          setIsUploadingImage(true);
          onImageUpload(file)
            .then((url) => {
              editor.chain().focus().setImage({ src: url }).run();
            })
            .catch(() => {
              // Upload failure surfacing (toast/etc.) is the consumer's responsibility.
            })
            .finally(() => {
              setIsUploadingImage(false);
            });
        }}
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) {
            onFileAttach?.(e.target.files);
            e.target.value = '';
          }
        }}
      />

      <style>{editorStyles}</style>

      <EditorContent
        editor={editor}
        className="flex-1 prose max-w-none px-3 py-2 richtext-editor-content"
        style={{
          minHeight: minHeight - 40,
          color: 'var(--theme-card-text)',
        }}
      />
    </div>
  );
};

RichTextEditor.displayName = 'RichTextEditor';
